import { initializeApp } from "firebase/app";
import { getFirestore, collection, addDoc, serverTimestamp } from "firebase/firestore";
import { emailSchema, phoneSchema, sourceSchema, generalInquirySchema, sponsorSchema } from "@/lib/validation";
import { getReferral } from "@/lib/referral";

const firebaseConfig = {
  apiKey: "AIzaSyDgBvXbenzjIyw-cRsCs3xJVqcsBNLAeIw",
  authDomain: "yrd-2026.firebaseapp.com",
  projectId: "yrd-2026",
  storageBucket: "yrd-2026.firebasestorage.app",
  messagingSenderId: "768567693507",
  appId: "1:768567693507:web:496a27b1694bf130eb1276",
  measurementId: "G-658RT113W0"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize Firestore with named database
export const db = getFirestore(app, "yrd2026");

// Helper functions for form submissions with validation
export const saveEmail = async (email: string, source: "general" | "sponsor" | "stay-in-know" | "early-access" | "hotel-packages" | "ticket-notify" | "email-blast") => {
  const validatedEmail = emailSchema.parse(email);
  const validatedSource = sourceSchema.parse(source);
  const ref = getReferral();
  return addDoc(collection(db, "emails"), {
    email: validatedEmail,
    source: validatedSource,
    ...(ref && { ref }),
    createdAt: serverTimestamp()
  });
};

export const savePhoneNumber = async (phone: string, source: "general" | "sponsor" | "stay-in-know" | "early-access" | "hotel-packages" | "ticket-notify" | "email-blast") => {
  const validatedPhone = phoneSchema.parse(phone);
  const validatedSource = sourceSchema.parse(source);
  const ref = getReferral();
  return addDoc(collection(db, "phone_numbers"), {
    phone: validatedPhone,
    source: validatedSource,
    ...(ref && { ref }),
    createdAt: serverTimestamp()
  });
};

export const saveGeneralInquiry = async (data: {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  message: string;
}) => {
  const validated = generalInquirySchema.parse(data);
  return addDoc(collection(db, "general_inquiries"), {
    ...validated,
    createdAt: serverTimestamp()
  });
};

export const saveSponsor = async (data: {
  firstName: string;
  lastName: string;
  company: string;
  email: string;
  phone: string;
  message: string;
}) => {
  const validated = sponsorSchema.parse(data);
  return addDoc(collection(db, "sponsors"), {
    ...validated,
    createdAt: serverTimestamp()
  });
};

// ── n8n Webhook Helpers (dual-write) ──────────────────────────────

const N8N_BASE = "https://n8n-roostertail-u60710.vm.elestio.app/webhook";
const N8N_BASE_SS = "https://n8n-roostertail-u60710.vm.elestio.app/webhook";

export const n8nSaveEmail = async (email: string, source: string) => {
  const validatedEmail = emailSchema.parse(email);
  const ref = getReferral();
  return fetch(`${N8N_BASE}/yrd-waitlist`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email: validatedEmail, source, ...(ref && { ref }) }),
  });
};

export const n8nSavePhone = async (phone: string, source: string) => {
  const validatedPhone = phoneSchema.parse(phone);
  const ref = getReferral();
  return fetch(`${N8N_BASE}/yrd-phone`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ phone: validatedPhone, source, ...(ref && { ref }) }),
  });
};

export const n8nSaveInquiry = async (data: {
  email: string;
  phone: string;
  firstName: string;
  lastName: string;
  message: string;
}) => {
  const validated = generalInquirySchema.parse(data);
  return fetch(`${N8N_BASE}/yrd-inquiry`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(validated),
  });
};

export const n8nSaveSponsor = async (data: {
  email: string;
  phone: string;
  firstName: string;
  lastName: string;
  company: string;
  message: string;
}) => {
  const validated = sponsorSchema.parse(data);
  return fetch(`${N8N_BASE}/yrd-sponsor`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(validated),
  });
};

// ── Second n8n Webhook Set (SS) ──────────────────────────────

export const n8nSaveEmailSS = async (email: string, source: string) => {
  const validatedEmail = emailSchema.parse(email);
  const ref = getReferral();
  return fetch(`${N8N_BASE_SS}/yrd-waitlist-ss`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email: validatedEmail, source, ...(ref && { ref }) }),
  });
};

export const n8nSavePhoneSS = async (phone: string, source: string) => {
  const validatedPhone = phoneSchema.parse(phone);
  const ref = getReferral();
  return fetch(`${N8N_BASE_SS}/yrd-phone-ss`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ phone: validatedPhone, source, ...(ref && { ref }) }),
  });
};

export const n8nSaveInquirySS = async (data: {
  email: string;
  phone: string;
  firstName: string;
  lastName: string;
  message: string;
}) => {
  const validated = generalInquirySchema.parse(data);
  return fetch(`${N8N_BASE_SS}/yrd-inquiry-ss`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(validated),
  });
};

export const n8nSaveSponsorSS = async (data: {
  email: string;
  phone: string;
  firstName: string;
  lastName: string;
  company: string;
  message: string;
}) => {
  const validated = sponsorSchema.parse(data);
  return fetch(`${N8N_BASE_SS}/yrd-sponsor-ss`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(validated),
  });
};

// ── SlickText Phone Webhook ──────────────────────────────

const stripPhone = (phone: string): string => {
  const digits = phone.replace(/\D/g, "");
  return digits.startsWith("1") && digits.length === 11 ? digits.slice(1) : digits;
};

export const n8nSavePhoneSlickText = async (phone: string) => {
  const validatedPhone = phoneSchema.parse(phone);
  return fetch(`${N8N_BASE}/yrd-phone-slicktext`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ phone: stripPhone(validatedPhone) }),
  });
};

// Meta Conversions API helper
export const sendMetaLeadEvent = async (
  data: { email?: string; phone?: string },
  source: string = "early-access"
) => {
  const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
  if (!projectId) {
    console.error("Missing VITE_SUPABASE_PROJECT_ID for Meta Conversions API");
    return;
  }
  const url = `https://${projectId}.supabase.co/functions/v1/meta-conversions`;
  return fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ...data, source }),
  });
};
